import React, { useState } from 'react';
import { Send, Loader2, MessageCircle, Phone } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { API_BASE, TREATMENTS, SERVICES, COUNTRIES, LANGUAGES, TIME_PERIODS, CONTACT_METHODS } from './inquiry/constants';
import { InquiryStep3 } from './inquiry/InquiryStep3';
import { InquiryStep2 } from './inquiry/InquiryStep2';
import { InquiryStep1 } from './inquiry/InquiryStep1';
export default function InquiryForm({ isOpen, onClose, triggerSource = 'modal' }) {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Info
    full_name: '',
    email: '',
    phone: '',
    country: '',
    language: 'en',
    
    // Medical Info
    treatment: '',
    medical_condition: '',
    
    // Services
    services: [],
    
    // Preferences
    preferred_contact: 'whatsapp',
    preferred_time: '',
    custom_time: '',
    
    // Additional
    additional_notes: '',
    consent: false,
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const validateStep = (stepNum) => {
    const newErrors = {};
    
    if (stepNum === 1) {
      if (!formData.full_name.trim()) newErrors.full_name = 'Name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
      if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
      if (!formData.country) newErrors.country = 'Country is required';
    }
    
    if (stepNum === 2) {
      if (!formData.treatment) newErrors.treatment = 'Please select a treatment';
    }
    
    if (stepNum === 3) {
      if (!formData.preferred_time) newErrors.preferred_time = 'Please select a time period';
      if (!formData.consent) newErrors.consent = 'Please accept the terms';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/public/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          source: triggerSource,
          submitted_at: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Thank you! Your inquiry has been submitted successfully. Our team will contact you soon.', {
          duration: 5000,
        });
        onClose();
        // Reset form
        setFormData({
          full_name: '',
          email: '',
          phone: '',
          country: '',
          language: 'en',
          treatment: '',
          medical_condition: '',
          services: [],
          preferred_contact: 'whatsapp',
          preferred_time: '',
          custom_time: '',
          additional_notes: '',
          consent: false,
        });
        setStep(1);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit inquiry. Please try again.');
      }
    } catch (error) {
      toast.error('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <InquiryStep1 errors={errors} formData={formData} handleChange={handleChange} />
  );

  const renderStep2 = () => (
    <InquiryStep2 errors={errors} formData={formData} handleChange={handleChange} handleServiceToggle={handleServiceToggle} />
  );

  const renderStep3 = () => (
    <InquiryStep3 errors={errors} formData={formData} handleChange={handleChange} />
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-center">
            Get Free Medical Consultation
          </DialogTitle>
          <DialogDescription className="text-center">
            Fill out the form and our team will get back to you within 24 hours
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 my-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  step >= s
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div className={`w-8 h-0.5 ${step > s ? 'bg-orange-500' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Form Content */}
        <div className="min-h-[300px]">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-3 mt-4 pt-4 border-t">
          {step > 1 && (
            <Button variant="outline" onClick={handleBack} className="flex-1">
              Back
            </Button>
          )}
          {step < 3 ? (
            <Button 
              onClick={handleNext} 
              className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
            >
              Continue
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
              data-testid="inquiry-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Inquiry
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Floating Bubble Component
export function InquiryFloatingBubble() {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);

  // Hide tooltip after 5 seconds
  React.useEffect(() => {
    const timer = setTimeout(() => setShowTooltip(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {/* Floating Bubble */}
      <div className="fixed bottom-6 right-6 z-50">
        {/* Tooltip */}
        {showTooltip && !isOpen && (
          <div className="absolute bottom-full right-0 mb-2 animate-bounce">
            <div className="bg-gray-900 text-white text-sm px-3 py-2 rounded-lg shadow-lg whitespace-nowrap">
              Get Free Consultation! 👋
              <div className="absolute bottom-0 right-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" />
            </div>
          </div>
        )}
        
        {/* Button */}
        <button
          onClick={() => setIsOpen(true)}
          className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-110 flex items-center justify-center group"
          data-testid="inquiry-floating-button"
        >
          <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7 text-white group-hover:scale-110 transition-transform" />
          
          {/* Ping animation */}
          <span className="absolute top-0 right-0 w-3 h-3 bg-green-500 rounded-full animate-ping" />
          <span className="absolute top-0 right-0 w-3 h-3 bg-green-500 rounded-full" />
        </button>
      </div>

      {/* Inquiry Form Modal */}
      <InquiryForm isOpen={isOpen} onClose={() => setIsOpen(false)} triggerSource="floating_bubble" />
    </>
  );
}
